import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate.jsx';
import Card from '../../../../../ui/canon/Card';
import { getDealRoomBrandedName } from '../../../utils/locale';

// Import components for each section
import DealEvaluateMatches from './DealEvaluateMatches';
import DealInvite from './DealInvite';
import DealReviewProposals from './DealReviewProposals';
import DealShortlisted from './DealShortlisted';
import DealNdaOutForReview from './DealNdaOutForReview';
import DealNdaAwaitingSignature from './DealNdaAwaitingSignature';
import DealNegotiate from './DealNegotiate';
import Loading from '../../common/Loading';
import ArrowTabs from './ArrowTabs';

// Error boundary to catch and handle component errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in DealProspects:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                <FormattedMessage id="succession_deals.wizard.steps.deal_prospects.error_boundary.title" />
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_prospects.error_boundary.description" />
                </p>
              </div>
              <div className="mt-4">
                <button 
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-succession-error bg-succession-error-bg hover:bg-succession-error-bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-error"
                >
                  <FormattedMessage id="succession_deals.wizard.steps.deal_prospects.error_boundary.refresh_button" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const DealProspects = ({ deal, refetchDeal, navigateTo, currentUser, company }) => {
  const location = useLocation();
  const intl = useIntl();
  const dealRoomBrandedName = getDealRoomBrandedName(company);
  
  // Show loading state if deal is undefined
  if (!deal) {
    return <Loading message={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.loading_message' })} />;
  }
  
  // Helper to safely access deal properties with fallbacks
  const safeCount = (property, defaultValue = 0) => {
    if (!deal) return defaultValue;
    return deal[property] !== undefined ? deal[property] : 
           Array.isArray(deal[property]) ? deal[property].length : defaultValue;
  };
  
  // Define the main navigation tabs
  const tabs = [
    { 
      id: 'teaser', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.teaser.label', defaultMessage: 'View Teaser' }),
      count: undefined,
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.teaser.description', defaultMessage: 'View the deal teaser' }),
      filters: []
    },
    { 
      id: 'matches', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.matches.label' }),
      count: safeCount('matches_count', 0),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.matches.description' }),
      filters: [
        { id: 'all', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }) },
        { id: 'matches', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.suggested_matches' }) },
        { id: 'rolodex', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.rolodex' }) },
        { id: 'follower', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.followers' }) },
        { id: 'saved', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.saved' }) }
      ]
    },
    { 
      id: 'invited', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.invited.label' }),
      count: safeCount('number_of_invites', 0),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.invited.description' }),
      filters: [
        { id: 'all', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }) },
        { id: 'saved', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.saved' }) }
      ]
    },
    { 
      id: 'reviewing', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.reviewing.label' }),
      count: safeCount('number_of_proposals', 0),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.reviewing.description' }),
      filters: [
        { id: 'all', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }) },
        { id: 'declined', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.declined' }) }
      ]
    },
    { 
      id: 'shortlisted', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.shortlisted.label' }),
      count: deal.shortlisted ? deal.shortlisted.length : 0,
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.shortlisted.description' }),
      filters: [
        { id: 'all', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }) },
        { id: 'declined', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.declined' }) }
      ]
    },
    { 
      id: 'ndas', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.ndas.label' }),
      count: safeCount('number_of_ndas', 0),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.ndas.description' }),
      filters: [
        { id: 'all', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }) },
        { id: 'out_for_signature', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.out_for_signature' }) },
        { id: 'awaiting_signature', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.awaiting_signature' }) },
        { id: 'completed', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.completed' }) }
      ]
    },
    { 
      id: 'negotiate', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.negotiate.label' }),
      count: safeCount('number_of_negotiations', 0),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.negotiate.description' }),
      filters: []
    }
  ];

  // State to track the active tab and filter
  const [activeTab, setActiveTab] = useState('matches');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Extract the tab from the URL path
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const urlTab = pathParts[pathParts.length - 1] || 'matches';
    
    if (tabs.some(tab => tab.id === urlTab)) {
      setActiveTab(urlTab);
      // Reset filter when changing tabs
      setActiveFilter('all');
    }
  }, [location.pathname]);

  // Handle tab changes
  const handleTabChange = (tabId) => {
    if (tabId === 'teaser') {
      navigateTo(`/succession/deals/${deal.deal_name}/teaser`);
    } else {
      navigateTo(`/succession/deals/${deal.deal_name}/prospects/${tabId}`);
    }
    setActiveFilter('all'); // Reset filter when changing tabs
  };

  // Handle filter changes
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
  };

  // Get current tab's filters
  const currentTabFilters = tabs.find(tab => tab.id === activeTab)?.filters || [];

  // Render the appropriate content based on the active tab
  const renderContent = () => {
    // Create a safe object in case deal has null properties
    const safeDeal = deal || {};
    
    switch (activeTab) {
      case 'matches':
        return <DealEvaluateMatches 
                 deal={safeDeal} 
                 refetchDeal={refetchDeal} 
                 activeFilter={activeFilter}
                 filters={currentTabFilters}
                 onFilterChange={handleFilterChange}
               />;
      case 'invited':
        return <DealInvite
                 deal={safeDeal}
                 refetchDeal={refetchDeal}
                 activeFilter={activeFilter}
                 filters={currentTabFilters}
                 onFilterChange={handleFilterChange}
                 company={company}
               />;
      case 'reviewing':
        return <DealReviewProposals
                 deal={safeDeal}
                 refetchDeal={refetchDeal}
                 activeFilter={activeFilter}
                 filters={currentTabFilters}
                 onFilterChange={handleFilterChange}
                 currentUser={currentUser}
               />;
      case 'shortlisted':
        return <DealShortlisted 
                 deal={safeDeal} 
                 refetchDeal={refetchDeal} 
                 activeFilter={activeFilter}
                 filters={currentTabFilters}
                 onFilterChange={handleFilterChange}
                 currentUser={currentUser}
               />;
      case 'ndas':
        // Show appropriate NDA component based on the filter
        switch (activeFilter) {
          case 'out_for_signature':
            return <DealNdaOutForReview deal={safeDeal} refetchDeal={refetchDeal} />;
          case 'awaiting_signature':
            return <DealNdaAwaitingSignature deal={safeDeal} refetchDeal={refetchDeal} />;
          case 'completed':
            return <DealNdaCompleted deal={safeDeal} refetchDeal={refetchDeal} />;
          default: // 'all'
            // When 'all' is selected, show all three NDA sections stacked
            if (activeFilter === 'all') {
              // If it's set to 'all', show all three NDA sections stacked
              return (
                <div className="space-y-12">
                  <section>
                    <DealNdaOutForReview deal={safeDeal} refetchDeal={refetchDeal} />
                  </section>
                  
                  <section>
                    <DealNdaAwaitingSignature deal={safeDeal} refetchDeal={refetchDeal} />
                  </section>
                </div>
              );
            }
            return <DealNdaOutForReview deal={safeDeal} refetchDeal={refetchDeal} />;
        }
      case 'negotiate':
        return <DealNegotiate deal={safeDeal} refetchDeal={refetchDeal} />;
      default:
        return <DealEvaluateMatches deal={safeDeal} refetchDeal={refetchDeal} />;
    }
  };

  // Get the current active tab info
  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession_deals.proposal.home', defaultMessage: 'Deal Prospects' }, { dealRoomName: dealRoomBrandedName })}
      subtitle={`#${deal?.deal_name || ''}`}
      variant="wide"
    >
      {/* Tab Navigation */}
      <Card className="p-3 mb-4">
        <ArrowTabs
          tabs={tabs.map(tab => ({
            ...tab,
            isActive: activeTab === tab.id
          }))}
          onTabChange={handleTabChange}
        />
      </Card>

      {/* Content Area */}
      <Card className="p-6 overflow-x-auto">
        <ErrorBoundary>
          {renderContent()}
        </ErrorBoundary>
      </Card>
    </SingleColumnTemplate>
  );
};

DealProspects.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  company: PropTypes.object
};

export default DealProspects; 